import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { llmConfigurationService, type LLMConfiguration } from '../../../../components/api/llmConfigurationService';
import { logInvokeProfiles } from './invokeProfilesLogger';
import { invokeProfilesApi, type InvokeKind, type InvokeProfile } from './invokeProfilesApi';

const INVOKE_OPTIONS: { value: InvokeKind; label: string }[] = [
  { value: 'chat_completions', label: 'Chat completions' },
  { value: 'images_generations', label: 'Image generation' },
];

function displayName(llm: LLMConfiguration): string {
  if (llm.additionalConfigJson) {
    try {
      const parsed = JSON.parse(llm.additionalConfigJson);
      if (parsed?.displayName) return String(parsed.displayName);
    } catch {
      /* keep model name */
    }
  }
  return llm.modelName || llm.id;
}

const AddInvokeProfileModal: React.FC<{
  onClose: () => void;
  onSaved: (profiles: InvokeProfile[]) => void;
  initial?: InvokeProfile | null;
}> = ({ onClose, onSaved, initial }) => {
  const isEdit = Boolean(initial?.id);
  const [name, setName] = useState(initial?.name || '');
  const [invoke, setInvoke] = useState<InvokeKind>(initial?.invoke || 'images_generations');
  const [matchText, setMatchText] = useState((initial?.match || []).join('\n'));
  const [llmId, setLlmId] = useState(initial?.llm_id || '');
  const [enabled, setEnabled] = useState(initial?.enabled !== false);
  const [llms, setLlms] = useState<LLMConfiguration[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    llmConfigurationService.getList()
      .then((list) => setLlms(Array.isArray(list) ? list : []))
      .catch(() => setLlms([]));
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleSubmit = async () => {
    const match = matchText.split('\n').map((item) => item.trim()).filter(Boolean);
    if (!name.trim() || (!llmId && match.length === 0)) {
      setError('Set a name and either a match pattern or an LLM.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const profiles = await invokeProfilesApi.save(
        { name: name.trim(), match, llm_id: llmId, invoke, enabled },
        initial?.id
      );
      onSaved(profiles);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save invoke profile.';
      logInvokeProfiles('error', 'modal_save_failed', { message });
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex justify-center items-center p-4">
      <div className="bg-neutral-900 w-full max-w-lg rounded-xl shadow-xl border border-neutral-800">
        <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-800">
          <h2 className="text-white text-lg font-semibold">{isEdit ? 'Edit Invoke Profile' : 'Add Invoke Profile'}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4 text-sm text-white">
          <div>
            <label className="text-gray-400 block mb-1">Name</label>
            <input value={name} onChange={(event) => setName(event.target.value)} className="w-full bg-neutral-800 px-3 py-2 rounded outline-none" />
          </div>
          <div>
            <label className="text-gray-400 block mb-1">Invoke</label>
            <select value={invoke} onChange={(event) => setInvoke(event.target.value as InvokeKind)} className="w-full bg-neutral-800 px-3 py-2 rounded outline-none">
              {INVOKE_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-gray-400 block mb-1">Match patterns</label>
            <textarea
              value={matchText}
              onChange={(event) => setMatchText(event.target.value)}
              placeholder="gpt-image-*&#10;dall-e-*"
              rows={3}
              className="w-full bg-neutral-800 px-3 py-2 rounded outline-none"
            />
          </div>
          <div>
            <label className="text-gray-400 block mb-1">LLM (optional)</label>
            <select value={llmId} onChange={(event) => setLlmId(event.target.value)} className="w-full bg-neutral-800 px-3 py-2 rounded outline-none">
              <option value="">Any matching model</option>
              {llms.map((llm) => (
                <option key={llm.id} value={llm.id}>{displayName(llm)}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-gray-300">
            <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
            Enabled
          </label>
          {error ? <p className="text-red-400 text-xs">{error}</p> : null}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-800">
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white text-sm" disabled={saving}>Cancel</button>
          <button type="button" onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddInvokeProfileModal;
